import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit'
import { adminApi } from '@/lib/api'
import { apiThunk } from '@/app/thunk'
import type { RootState } from '@/app/store'
import type { ExceptionLog } from '@/types'

export interface ExceptionsState {
  items: ExceptionLog[]
  loading: boolean
  error: string | null
  search: string
}

const initialState: ExceptionsState = { items: [], loading: false, error: null, search: '' }

export const fetchExceptions = apiThunk<ExceptionLog[]>('exceptions/fetch', async (_arg, config) => {
  const rows = await adminApi.listExceptions(config)
  return rows ?? []
})

const exceptionsSlice = createSlice({
  name: 'exceptions',
  initialState,
  reducers: {
    exceptionSearchChanged(state, action: PayloadAction<string>) {
      state.search = action.payload
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchExceptions.pending, (state) => {
        state.loading = true
      })
      .addCase(fetchExceptions.fulfilled, (state, action) => {
        state.loading = false
        state.error = null
        state.items = action.payload
      })
      .addCase(fetchExceptions.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload ?? 'Could not load exceptions'
      })
  }
})

export const { exceptionSearchChanged } = exceptionsSlice.actions

const selectAll = (s: RootState): ExceptionLog[] => s.exceptions.items

export const selectVisibleExceptions = createSelector(
  [selectAll, (s: RootState) => s.exceptions.search],
  (items, search) => {
    const q = search.trim().toLowerCase()
    const sorted = [...items].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    )
    if (!q) return sorted
    return sorted.filter((e) =>
      ((e.user_email ?? '') + ' ' + (e.exception ?? '')).toLowerCase().includes(q)
    )
  }
)

/** How many landed in the last 24 hours — the number worth reacting to. */
export const selectRecentExceptionCount = createSelector([selectAll], (items) => {
  const cutoff = Date.now() - 24 * 60 * 60 * 1000
  return items.filter((e) => new Date(e.created_at).getTime() >= cutoff).length
})

export default exceptionsSlice.reducer
